import { Alert, Avatar, Button, Modal, Popover, Slider, Space, Spin, Typography } from "antd";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import axiosInstance from "./api";
import cropSquareImage from "./cropSquareImage";

export default function ProfilePicture({ employeeId, pictureRef, onUploaded }) {
  const inputRef = useRef();
  const [image, setImage] = useState(null);
  const [source, setSource] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(
    () => () => {
      if (image) URL.revokeObjectURL(image);
    },
    [image],
  );
  useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source);
    },
    [source],
  );
  useEffect(() => {
    let cancelled = false;
    if (!pictureRef) {
      setImage(null);
      return;
    }
    setLoading(true);
    axiosInstance
      .get(`employee/${employeeId}/picture`, { responseType: "blob" })
      .then(({ data }) => {
        if (!cancelled) setImage(URL.createObjectURL(data));
      })
      .catch(() => {
        if (!cancelled) setError("Impossibile caricare la foto.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, pictureRef]);

  const selectFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Seleziona un’immagine JPG, PNG o WebP.");
      return;
    }
    setError(null);
    setArea(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSource(URL.createObjectURL(file));
  };
  const upload = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await cropSquareImage(source, area);
      const body = new FormData();
      body.append("file", blob, "picture.png");
      await axiosInstance.patch(`employee/${employeeId}/picture`, body);
      setImage(URL.createObjectURL(blob));
      setSource(null);
      onUploaded?.();
    } catch {
      setError("Impossibile salvare la foto. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Space orientation="vertical" align="center">
        <Popover
          placement={"bottom"}
          content={
            <>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={selectFile}
                style={{ display: "none" }}
                aria-label="Seleziona foto dipendente"
              />
              <Button
                icon={<UploadOutlined />}
                disabled={loading || busy}
                onClick={() => inputRef.current.click()}
                type={"text"}
              >
                {image ? "Cambia foto" : "Carica foto"}
              </Button>
              {!source && error && <Alert type="error" title={error} />}
            </>
          }
        >
          <Spin spinning={loading}>
            <Avatar
              style={{ cursor: "pointer" }}
              shape="square"
              size={150}
              src={image}
              icon={<UserOutlined />}
            />
          </Spin>
        </Popover>
      </Space>
      <Modal
        title="Ritaglia foto"
        open={!!source}
        onCancel={() => {
          if (!busy) setSource(null);
        }}
        onOk={upload}
        confirmLoading={busy}
        okButtonProps={{ disabled: !area || busy }}
        okText="Salva foto"
        cancelText="Annulla"
        maskClosable={!busy}
        closable={!busy}
        destroyOnHidden
      >
        {error && <Alert type="error" title={error} style={{ marginBottom: 16 }} />}
        <div style={{ position: "relative", height: 360, background: "#222" }}>
          {source && (
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape={"rect"}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setArea(pixels)}
              mediaProps={{
                onError: () => {
                  setArea(null);
                  setError("Impossibile leggere l’immagine. Seleziona un altro file.");
                },
              }}
            />
          )}
        </div>
        <Typography.Text>Zoom</Typography.Text>
        <Slider
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={setZoom}
          disabled={busy}
          aria-label="Zoom foto"
        />
      </Modal>
    </>
  );
}
